import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Badge from '../../components/shared/Badge';
import { Mail, X, ArrowLeft, Filter, Search, ChevronDown, Plus, Loader2 } from 'lucide-react';
import ActionButton from '../../components/shared/ActionButton';
import { EventDataPopup } from '../../components/modules/events/EventDataPopup';
import { apiConnecter } from '../../services/apiConnector';
import { useSelector } from 'react-redux';
import { FaArrowLeft } from 'react-icons/fa';
import IconButton from '../../components/shared/IconButton';
import { DownloadIcon2 } from '../../assets/icons/icons';
import { BiExport } from "react-icons/bi";
/**
 * @typedef {Object} FilterItem
 * @property {number} id - Unique ID for the filter input element.
 * @property {string} field - The database field name selected for filtering.
 * @property {string} value - The search value for the selected field.
 * @property {boolean} isDropdownOpen - Controls the visibility of the field selection dropdown.
 */
/**
* @typedef {Object} DataItem
* @property {string} _id - Unique identifier for the data entry.
* @property {string} name - User's name.
* @property {string} email - User's email address.
* @property {string} createdAt - Timestamp of the entry creation.
* @property {string} extraFormData - JSON string containing extra dynamic form data.
*/
/**
* @typedef {Object} ExportData
* @property {DataItem[]} filteredData - Array of data entries to export.
* @property {Object[]} filterInfo - Array of currently active filter parameters.
*/
const MAX_HEIGHT_CALC = 'max-h-[calc(100vh-100px)]';
/**
 * Component for viewing and filtering captured data (e.g., feedback or registration)
 * for a specific event ID, including pagination and CSV export functionality.
 * @returns {JSX.Element} The Event Data Viewer UI.
 */
function EventDataViewer() {
    /* ****************************************************************
    * ROUTER HOOKS & CONTEXT *
    ******************************************************************/
    const navigate = useNavigate();
    const userInfo = useSelector((state) => state.userInfo);
    // Get eventId from URL search parameters
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get("eventId");
    /* ****************************************************************
    * STATES *
    ******************************************************************/
    /** @type {[DataItem[], React.Dispatch<React.SetStateAction<DataItem[]>>]} */
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [eventName, setEventName] = useState("Event Data");
    // Updated: Store the full API response with keys and values
    const [filterFieldsData, setFilterFieldsData] = useState(null);
    const [filters, setFilters] = useState([
        { id: Date.now(), field: '', value: '', isDropdownOpen: false, isValueDropdownOpen: false }
    ]);
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(50); // Items per page
    const dropdownRefs = useRef({});
    const valueDropdownRefs = useRef({});
    /* ****************************************************************
    * MODAL & UI HELPERS *
    ******************************************************************/
    /**
    * @returns {string} Tailwind CSS classes for the data badge style.
    */
    const getBadgeStyle = () => 'bg-[var(--color-input-placeholder)] text-white';
    /**
    * Opens the detail modal with the selected user's data.
    * @param {DataItem} item - The data item to display in the modal.
    */
    const openModal = (item) => {
        setSelectedUser(item);
        setIsModalOpen(true);
    };
    /**
    * Closes the detail modal.
    */
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };
    /* ****************************************************************
    * EFFECT HOOKS *
    ******************************************************************/
    // Effect for handling outside clicks to close filter dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(dropdownRefs.current).forEach(filterId => {
                if (dropdownRefs.current[filterId] && !dropdownRefs.current[filterId].contains(event.target)) {
                    setFilters(prev => prev.map(f =>
                        f.id === parseInt(filterId) ? { ...f, isDropdownOpen: false } : f
                    ));
                }
            });
            Object.keys(valueDropdownRefs.current).forEach(filterId => {
                if (valueDropdownRefs.current[filterId] && !valueDropdownRefs.current[filterId].contains(event.target)) {
                    setFilters(prev => prev.map(f =>
                        f.id === parseInt(filterId) ? { ...f, isValueDropdownOpen: false } : f
                    ));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Effect to fetch data and filter fields when eventId, page, or limit changes
    useEffect(() => {
        if (eventId) {
            getData(filters, currentPage, limit);
            getFilterFields();
        }
    }, [eventId, currentPage, limit]);
    /* ****************************************************************
    * SERVICES *
    ******************************************************************/
    /**
    * Fetches paginated and filtered event data from the API.
    * @param {FilterItem[]} [filtersArray=[]] - The array of filters to apply.
    * @param {number} [page=currentPage] - The page number to fetch.
    * @param {number} [itemsPerPage=limit] - The number of items per page.
    */
    const getData = async (filtersArray = [], page = currentPage, itemsPerPage = limit) => {
        if (!eventId) return;
        setLoading(true);
        try {
            const filterParams = {
                page,
                limit: itemsPerPage
            };
            if (Array.isArray(filtersArray) && filtersArray.length > 0) {
                const validFilters = filtersArray.filter(f => f.field && f.value);
                if (validFilters.length > 0) {
                    filterParams.filters = validFilters.map(f => ({
                        filterField: f.field.trim(),
                        filterValue: f.value.trim()
                    }));
                }
            }
            const response = await apiConnecter("POST", `UNITE_US_GCU/GetFeedbackDataUNITE_US_GCU/${eventId}`, filterParams, {
                authorization: `Bearer ${userInfo.token}`,
            });
            if (response?.data?.status) {
                const apiResult = response.data.result;
                const items = apiResult?.result || [];
                setData(items);
                // Attempt to set event name from the first item, fallback to a truncated ID
                setEventName(apiResult.result[0]?.eventId?.eventName || `Event: ${eventId.substring(0, 8)}...`);
                const pagination = apiResult.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalCount(pagination?.totalCount || '0')
            } else {
                setData([]);
                setTotalPages(1);
                setEventName(`Event: ${eventId.substring(0, 8)}...`);
            }
        } catch (error) {
            console.error("Error fetching event data:", error.response?.data?.message || error.message);
            setData([]);
            setTotalPages(1);
            setEventName(`Event: ${eventId.substring(0, 8)}...`);
        } finally {
            setLoading(false);
        }
    };

    // const getData = async (filtersArray = [], page = currentPage, itemsPerPage = limit) => {
    //     if (!eventId) return;
    //     setLoading(true);
    //     try {
    //         const filterParams = {
    //             page,
    //             limit: itemsPerPage
    //         };
    //         if (Array.isArray(filtersArray) && filtersArray.length > 0) {
    //             const validFilters = filtersArray.filter(f => f.field && f.value);
    //             if (validFilters.length > 0) {
    //                 filterParams.filters = validFilters.map(f => ({
    //                     filterField: f.field.trim(),
    //                     filterValue: f.value.trim()
    //                 }));
    //             }
    //         }
    //         const response = await apiConnecter("POST", `UNITE_US_GCU/GetFeedbackDataUNITE_US_GCU/${eventId}`, filterParams, {
    //             authorization: `Bearer ${userInfo.token}`,
    //         });
    //         if (response?.data?.status) {
    //             const apiResult = response.data.result;
    //             const items = apiResult?.result || [];
    //             setData(items);
    //             // Attempt to set event name from the first item, fallback to a truncated ID
    //             setEventName(apiResult.result[0]?.eventId?.eventName || `Event: ${eventId.substring(0, 8)}...`);
    //             const pagination = apiResult.pagination;
    //             setTotalPages(pagination?.totalPages || 1);
    //             setTotalCount(pagination?.totalCount || '0')
    //         } else {
    //             setData([]);
    //             setTotalPages(1);
    //             setEventName(`Event: ${eventId.substring(0, 8)}...`);
    //         }
    //     } catch (error) {
    //         console.error("Error fetching event data:", error.response?.data?.message || error.message);
    //         setData([]);
    //         setTotalPages(1);
    //         setEventName(`Event: ${eventId.substring(0, 8)}...`);
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    /**
    * Fetches the list of available fields that can be used for data filtering from the API.
    */
    const getFilterFields = async () => {
        try {
            const response = await apiConnecter("GET", `UNITE_US_GCU/searchfieldsFeedbackForm/${eventId}`, "", {
                authorization: `Bearer ${userInfo.token}`,
            });
            if (response.data && response.data.result) {
                // Store the complete response with keys and values
                setFilterFieldsData(response.data.result);
            }
        } catch (error) {
            console.error("Error fetching filter fields:", error.response?.data?.message || error.message);
        }
    };
    /* ****************************************************************
    * FILTERS LOGIC *
    ******************************************************************/
    /**
    * Triggers a data fetch with the current filter settings, resetting to the first page.
    */
    const handleFilterChange = async () => {
        setCurrentPage(1); // Reset to first page on filter change
        await getData(filters, 1, limit);
    };
    /**
    * Resets all filters to their initial state and fetches unfiltered data for the first page.
    */
    const handleResetFilter = () => {
        setFilters([{ id: Date.now(), field: '', value: '', isDropdownOpen: false, isValueDropdownOpen: false }]);
        setCurrentPage(1);
        getData([], 1, limit);
    };
    /**
    * Updates the selected field for a specific filter item.
    * @param {number} filterId - The ID of the filter item to update.
    * @param {string} field - The new field name.
    */
    const handleFieldSelect = (filterId, field) => {
        setFilters(prev => {
            const updatedFilters = prev.map(f =>
                f.id === filterId ? { ...f, field, value: '', isDropdownOpen: false, isValueDropdownOpen: false } : f
            );
            // Call API with updated filter values

            return updatedFilters;
        });
    };

    /**
    * Toggles the visibility of a filter's field selection dropdown.
    * @param {number} filterId - The ID of the filter item.
    */
    const toggleDropdown = (filterId) => {
        setFilters(prev => prev.map(f =>
            f.id === filterId ? { ...f, isDropdownOpen: !f.isDropdownOpen, isValueDropdownOpen: false } : f
        ));
    };
    /**
    * Toggles the visibility of a filter's value selection dropdown.
    * @param {number} filterId - The ID of the filter item.
    */
    const toggleValueDropdown = (filterId) => {
        setFilters(prev => prev.map(f =>
            f.id === filterId ? { ...f, isValueDropdownOpen: !f.isValueDropdownOpen, isDropdownOpen: false } : f
        ));
    };
    /**
    * Updates the selected value for a specific filter item.
    * @param {number} filterId - The ID of the filter item to update.
    * @param {string} value - The new value.
    */
    const handleValueSelect = (filterId, value) => {
        // Update filter value and close the value dropdown
        setFilters(prev => {
            const updatedFilters = prev.map(f =>
                f.id === filterId ? { ...f, value, isValueDropdownOpen: false } : f
            );
            // Call API with updated filter values
            getData(updatedFilters, currentPage, limit); // Fetch data on value select
            return updatedFilters;
        });
    };

    /**
    * Updates the search value for a specific filter item.
    * @param {number} filterId - The ID of the filter item to update.
    * @param {string} value - The new search value.
    */
    const updateFilterValue = (filterId, value) => {
        setFilters(prev => {
            const updatedFilters = prev.map(f =>
                f.id === filterId ? { ...f, value } : f
            );
            // Call API with updated filter values
            // getData(updatedFilters, currentPage, limit); // Fetch data on value change
            return updatedFilters;
        });
    };

    /**
    * Adds a new empty filter item to the filters array.
    */
    const addFilter = () => {
        setFilters(prev => [...prev, { id: Date.now(), field: '', value: '', isDropdownOpen: false, isValueDropdownOpen: false }]);
    };
    /**
    * Removes a filter item from the filters array, unless it is the last remaining one.
    * @param {number} filterId - The ID of the filter item to remove.
    */
    // const removeFilter = (filterId) => {
    //     if (filters.length > 1) {
    //         setFilters(prev => prev.filter(f => f.id !== filterId));
    //     }
    // };

    const removeFilter = (filterId) => {
        if (filters.length <= 1) return; // keep your guard

        setFilters(prev => {
            const next = prev.filter(f => f.id !== filterId);
            setCurrentPage(1);
            getData(next, 1, limit); // refetch with remaining filters
            return next;
        });
    };

    /* ****************************************************************
    * HELPER FUNCTIONS FOR NEW FUNCTIONALITY *
    ******************************************************************/
    // Get available keys from the API response
    const getAvailableKeys = () => {
        if (!filterFieldsData) return [];
        return Object.keys(filterFieldsData).filter(key =>
            key !== 'status' && key !== 'message'
        );
    };
    // Get values for a specific key
    const getValuesForKey = (key) => {
        if (!filterFieldsData || !key) return [];
        const values = filterFieldsData[key];
        if (Array.isArray(values)) {
            // Remove duplicates and empty values
            return [...new Set(values)].filter(v => v && v.toString().trim() !== '');
        }
        return [];
    };
    /* ****************************************************************
    * EXPORT LOGIC *
    ******************************************************************/
    /**
    * Creates and triggers a download for a CSV file based on the provided data.
    * It dynamically generates headers from all unique keys in the 'extraFormData'.
    * @param {ExportData} exportData - Object containing the data to export.
    */
    const exportToCSVRow = (exportData) => {
        const { filteredData } = exportData;
        let csvContent = "";
        if (!filteredData || filteredData.length === 0) {
            alert("No data to export");
            return;
        }
        const cleanData = (str) => {
            return (str || '').replace(/[^a-zA-Z0-9 /@.+:]/g, '').trim(); // Only allows letters, numbers, and spaces
        };
        // Collect and normalize all dynamic keys from extraFormData
        const allExtraKeys = new Set();
        filteredData.forEach((item) => {
            const extra = item.extraFormData ? JSON.parse(item.extraFormData) : {};
            Object.keys(extra).forEach((key) => allExtraKeys.add(key.trim()));
        });
        // Sort dynamic keys alphabetically
        const dynamicFields = Array.from(allExtraKeys).sort();
        // Build headers
        const headers = ['Name', 'Email', ...dynamicFields, 'Created At'];
        csvContent += headers.map(cleanData).join(',') + '\n';
        // Build rows safely
        filteredData.forEach((item) => {
            const extra = item.extraFormData ? JSON.parse(item.extraFormData) : {};
            // Replace commas in dynamic values to avoid breaking the CSV structure
            const dynamicValues = dynamicFields.map((key) => cleanData(extra[key]));
            const row = [
                cleanData(item.name),
                cleanData(item.email),
                ...dynamicValues,
                cleanData(item.createdAt),
            ].join(',');
            csvContent += row + '\n';
        });
        // Trigger CSV download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${eventName.replace(/[^a-z0-9]/gi, '_')}_data_export.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    /**
    * Handles the export process by fetching the *full* (un-paginated) data
    * based on current filters and then passing it to the CSV creation function.
    * @param {FilterItem[]} [filtersArray=[]] - The current array of filters to apply for the full export.
    */
    async function handleExport(filtersArray = []) {
        try {
            const filterParams = {};
            if (Array.isArray(filtersArray) && filtersArray.length > 0) {
                const validFilters = filtersArray.filter(f => f.field && f.value);
                if (validFilters.length > 0) {
                    filterParams.filters = validFilters.map(f => ({
                        filterField: f.field.trim(),
                        filterValue: f.value.trim()
                    }));
                }
            }
            // API endpoint to get ALL data for CSV (no page/limit parameters needed)
            const response = await apiConnecter("POST", `UNITE_US_GCU/GetCSVForFeedbackDataUNITE_US_GCU/${eventId}`, filterParams, {
                authorization: `Bearer ${userInfo.token}`,
            });
            if (response?.data?.status) {
                const apiResult = response.data.result || [];
                if (apiResult && apiResult.length > 0) {
                    const exportData = {
                        filteredData: apiResult,
                        filterInfo: filters.filter(f => f.field && f.value).map(f => ({
                            field: f.field,
                            value: f.value
                        })),
                    };
                    exportToCSVRow(exportData);
                } else {
                    alert("No data available for the current filters to export.");
                }
            } else {
                console.error("API call failed during export.");
                alert("Failed to retrieve data for export. Please try again.");
            }
        } catch (e) {
            console.error("Export error:", e);
            alert("An error occurred during the export process.");
        }
    }
    /* ****************************************************************
    * PAGINATION LOGIC *
    ******************************************************************/
    /**
    * Changes the current page for the data table.
    * @param {number} newPage - The new page number to navigate to.
    */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };
    /* ****************************************************************
    * RENDER LOGIC *
    ******************************************************************/
    const hasActiveFilters = filters.some(f => f.field || f.value);
    const canSearch = filters.some(f => f.field && f.value);
    function getFormetdTime(d) {
        const date = new Date(d).toLocaleDateString();
        return date;
    }
    const availableKeys = getAvailableKeys();
    return (
        <div className="text-[var(--primary-text-color)] m-4 w-full">
            <main>
                <header className="flex w-full items-center justify-start mb-6 flex-wrap gap-1.5">
                    {/* Back Button */}
                    <h2 className="text-2xl font-semibold text-[var(--primary-text-color)] flex gap-3 cursor-pointer">
                        <span className='mt-1' onClick={() => navigate(-1)}><FaArrowLeft /></span>
                    </h2>
                    {/* Event Name Title */}
                    <div className="flex-1">
                        <h2 className="md:text-2xl ml-2.5 font-semibold text-[var(--primary-text-color)] text-xl truncate">
                            Captured Data for: {eventName}
                        </h2>
                    </div>
                    {/* <div className="flex-1">
                        <h3 className="md:text-2xl ml-2.5 font-semibold text-[var(--primary-text-color)] text-xl truncate">
                            Feedback Count: {totalCount}
                        </h3>
                    </div> */}
                    {/* Export Button */}
                    <div className="flex gap-2 flex-wrap justify-between mb-2">
                        <IconButton
                            icon={BiExport}
                            label="Export"
                            onClick={() => handleExport(filters)}
                            hoverColor="hover:bg-gray-300"
                            bgColor="bg-[var(--analytics-card-color)]"
                            border={true}
                            color='black'
                        />
                    </div>
                </header>
                {/* Multiple Filters Section */}
                <div className="bg-[var(--color-surface-background)] rounded-2xl p-4 border border-[var(--border-color)] mb-4">
                    <div className="space-y-3">
                        {/* Filter Action Buttons */}
                        <div className='flex justify-between flex-col sm:flex-row'>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={addFilter}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-[var(--color-surface-background)] border border-[var(--border-color)] hover:border-[var(--color-active)] rounded-full text-sm font-medium transition-colors cursor-pointer"
                                    title="Add another filter"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Filter
                                </button>
                                <button
                                    className="px-6 py-2.5 bg-gradient-to-r bg-[var(--color-primary)] text-white rounded-full text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    onClick={handleFilterChange}
                                    disabled={!canSearch}
                                >
                                    Search
                                </button>
                                {hasActiveFilters && (
                                    <button
                                        className="px-4 py-2.5 text-[var(--primary-text-color)] hover:opacity-80 text-sm font-medium transition-opacity cursor-pointer"
                                        onClick={handleResetFilter}
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                            <h3 className="inline-flex items-center justify-center px-2 py-1 rounded-md text-md font-medium bg-[var(--color-input-placeholder)] text-white mt-3 sm:mt-0 w-fit">
                                Results: {totalCount}
                            </h3>
                        </div>
                        {/* Individual Filter Rows */}
                        {filters.length > 0 && (
                            <div className="mt-4 space-y-3">
                                {filters.map((filter) => (
                                    <div key={filter.id} className="flex flex-wrap items-center gap-3">
                                        {/* Field Dropdown Selection */}
                                        <div className="relative flex-1 min-w-[180px]" ref={(el) => (dropdownRefs.current[filter.id] = el)}>
                                            <button
                                                onClick={() => toggleDropdown(filter.id)}
                                                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[var(--color-surface-background)] rounded-full border border-[var(--border-color)] hover:border-[var(--color-active)] transition-colors cursor-pointer"
                                            >
                                                <Filter className="w-4 h-4 text-[var(--primary-text-color)] opacity-60" />
                                                <span className="flex-1 text-left text-sm text-[var(--primary-text-color)]">
                                                    {filter.field || 'Filter by...'}
                                                </span>
                                                <ChevronDown
                                                    className={`w-4 h-4 text-[var(--primary-text-color)] opacity-60 transition-transform ${filter.isDropdownOpen ? 'rotate-180' : ''}`}
                                                />
                                            </button>
                                            {filter.isDropdownOpen && (
                                                <div className="absolute z-50 w-full mt-2 bg-[var(--color-surface-background)] border border-[var(--border-color)] rounded-2xl shadow-lg overflow-hidden">
                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                        {availableKeys.length === 0 ? (
                                                            <div className="px-4 py-3 text-sm text-[var(--primary-text-color)] opacity-50">
                                                                No fields available
                                                            </div>
                                                        ) : (
                                                            availableKeys.map((field, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => handleFieldSelect(filter.id, field)}
                                                                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${filter.field === field
                                                                        ? 'bg-[var(--color-primary)] text-white'
                                                                        : 'text-[var(--primary-text-color)] hover:bg-[var(--border-color)] hover:bg-opacity-20'
                                                                        }`}
                                                                >
                                                                    {field}
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Value Selection: Combined Input + Dropdown */}
                                        <div className="relative flex-1 min-w-[180px]" ref={(el) => (valueDropdownRefs.current[filter.id] = el)}>
                                            <div className="flex items-center gap-2 px-4 py-2.5 bg-[var(--color-surface-background)] rounded-full border border-[var(--border-color)] hover:border-[var(--color-active)] transition-colors">
                                                <Search className="w-4 h-4 text-[var(--primary-text-color)] opacity-60" />
                                                <input
                                                    type="text"
                                                    className="flex-1 border-0 bg-transparent text-sm focus:outline-none text-[var(--primary-text-color)] placeholder:text-[var(--primary-text-color)] placeholder:opacity-50"
                                                    placeholder={filter.field ? "Search or select value..." : "Select field first..."}
                                                    value={filter.value}
                                                    onChange={(e) => updateFilterValue(filter.id, e.target.value)}
                                                    onFocus={() => {
                                                        // Show dropdown when input is focused if there are predefined values
                                                        if (filter.field && getValuesForKey(filter.field).length > 0 && !filter.isValueDropdownOpen) {
                                                            setFilters(prev => prev.map(f =>
                                                                f.id === filter.id ? { ...f, isValueDropdownOpen: true } : f
                                                            ));
                                                        }
                                                    }}
                                                    style={{ background: 'transparent' }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleFilterChange();
                                                        }
                                                    }}
                                                />
                                                {filter.value && (
                                                    <button onClick={() => updateFilterValue(filter.id, '')}>
                                                        <X className="w-4 h-4 text-[var(--primary-text-color)] opacity-60 hover:opacity-100 transition-opacity" />
                                                    </button>
                                                )}
                                                {/* Show dropdown toggle only if there are predefined values */}
                                                {filter.field && getValuesForKey(filter.field).length > 0 && (
                                                    <button onClick={() => toggleValueDropdown(filter.id)}>
                                                        <ChevronDown
                                                            className={`w-4 h-4 text-[var(--primary-text-color)] opacity-60 transition-transform ${filter.isValueDropdownOpen ? 'rotate-180' : ''}`}
                                                        />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Dropdown with predefined values (if available) */}
                                            {filter.field && filter.isValueDropdownOpen && getValuesForKey(filter.field).length > 0 && (
                                                <div className="absolute z-50 w-full mt-2 bg-[var(--color-surface-background)] border border-[var(--border-color)] rounded-2xl shadow-lg overflow-hidden">
                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                        {getValuesForKey(filter.field)
                                                            // Filter dropdown options based on current input value
                                                            .filter(value =>
                                                                value.toLowerCase().includes(filter.value.toLowerCase())
                                                            )
                                                            .map((value, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => handleValueSelect(filter.id, value)}
                                                                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${filter.value === value
                                                                        ? 'bg-[var(--color-primary)] text-white'
                                                                        : 'text-[var(--primary-text-color)] hover:bg-[var(--border-color)] hover:bg-opacity-20'
                                                                        }`}
                                                                >
                                                                    {value}
                                                                </button>
                                                            ))}
                                                        {/* Show "No matches" if filter text doesn't match any options */}
                                                        {getValuesForKey(filter.field).filter(value =>
                                                            value.toLowerCase().includes(filter.value.toLowerCase())
                                                        ).length === 0 && filter.value && (
                                                                <div className="px-4 py-3 text-sm text-[var(--primary-text-color)] opacity-50">
                                                                    No matches found. Press Enter to search for "{filter.value}"
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Remove Filter Button */}
                                        {filters.length > 1 && (
                                            <button
                                                onClick={() => removeFilter(filter.id)}
                                                className="p-2.5 text-red-500 hover:bg-red-500 hover:text-white hover:bg-opacity-10 rounded-full transition-colors cursor-pointer"
                                                title="Remove filter"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {/* List / Table Section */}
                <div className="space-y-4">
                    {/* Mobile View List */}
                    <div className="flex flex-col space-y-4 lg:hidden">
                        {loading ? (
                            <div className="text-center py-4 bg-[var(--color-surface-background)] rounded-md">
                                <div className="loader"></div>
                            </div>
                        ) : data.length === 0 ? (
                            <div className="text-center text-black">No data entries found</div>
                        ) : (
                            data.map(item => {
                                const extraData = JSON.parse(item.extraFormData || '{}');
                                return (
                                    <div
                                        key={item._id}
                                        className="bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-4 shadow-md flex flex-col space-y-2"
                                    >
                                        <div className="flex justify-between items-start">
                                            <h3 className="text-lg font-semibold truncate text-[var(--primary-text-color)]">{item.name}</h3>
                                            <div className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium ${getBadgeStyle()}`}>
                                                {item.createdAt ? getFormetdTime(item.createdAt) : 'N/A'}
                                            </div>
                                        </div>
                                        <div className="text-[var(--primary-text-color)] text-sm">{item.email}</div>
                                        {/* <div className="grid grid-cols-2 gap-4 text-[var(--primary-text-color)] text-sm mt-2">
                                            <div>
                                                <div className="text-gray-400 text-xs">Position</div>
                                                <div className="font-semibold truncate text-xs sm:text-sm">
                                                    {extraData.position || 'N/A'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-gray-400 text-xs">Organization</div>
                                                <div className="font-semibold truncate text-xs sm:text-sm">
                                                    {extraData.organization || 'N/A'}
                                                </div>
                                            </div>
                                        </div> */}
                                        <button
                                            onClick={() => {
                                                // Passes the parsed JSON object to the modal
                                                openModal(extraData)
                                            }} className="mt-2 flex items-center justify-center p-2 rounded-lg text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] cursor-pointer transition-colors"
                                        >
                                            View More
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    {/* Desktop View Table */}
                    <div className={`hidden lg:block bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-5 overflow-auto custom-scrollbar ${MAX_HEIGHT_CALC} w-full`}>
                        <div className="overflow-x-auto">
                            <table className="w-full table-auto border-collapse">
                                {/* Table Headers */}
                                {data.length > 0 && (
                                    <thead className="bg-[var(--color-surface-background)] sticky top-0 z-10">
                                        <tr className='border-b border-[var(--border-color)] hover:bg-gray-750 '>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/4">Name</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/4">Email</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/4">Created At</th>
                                            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/4">Action</th>
                                        </tr>
                                    </thead>
                                )}
                                {/* Table Body */}
                                <tbody className="">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="4" className="py-8 text-center">
                                                <div className="loader">  </div>
                                            </td>
                                        </tr>
                                    ) : data.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="py-4 px-6 text-center text-black">No data entries found</td>
                                        </tr>
                                    ) : (
                                        data.map(item => (
                                            <tr key={item._id} className="border-b border-[var(--border-color)] hover:bg-gray-750">
                                                <td className="py-4 px-4 text-[var(--primary-text-color)] font-medium truncate">{item.name}</td>
                                                <td className="py-4 px-4 text-[var(--primary-text-color)] truncate text-sm">{item.email}</td>
                                                <td className="py-4 px-2">
                                                    <div className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium bg-[var(--color-input-placeholder)] text-white`}>
                                                        {item.createdAt ? getFormetdTime(item.createdAt) : 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4 flex space-x-2">
                                                    <ActionButton
                                                        label="View More"
                                                        // Parses the JSON string to pass the object to the modal
                                                        onClick={() => openModal(JSON.parse(item.extraFormData || '{}'))}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-[var(--primary-text-color)]">
                                Page {currentPage} of {totalPages}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${currentPage === 1
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-[var(--color-surface-background)] text-[var(--primary-text-color)] hover:bg-[var(--color-primary)] hover:text-white '
                                    }`}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <button
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${currentPage === totalPages
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-[var(--color-surface-background)] text-[var(--primary-text-color)] hover:bg-[var(--color-primary)] hover:text-white'
                                    }`}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </main>
            {/* Event Data Detail Modal */}
            {isModalOpen && selectedUser && (
                <EventDataPopup isOpen={isModalOpen} onClose={closeModal} user={selectedUser} />
            )}
        </div>
    );
}
export default EventDataViewer;